// Capa de CAMPAÑA estacional para el reproductor de signage. Se compone ENCIMA
// de las escenas (z bajo el ticker/header), en los MÁRGENES, sin tocar la zona
// central (precio/producto). Un solo <canvas> de partículas + acento/saludo.
// Ligero para box Android: pool de partículas, 1 rAF, pausa si oculto, respeta
// prefers-reduced-motion (dibuja un frame estático). Prototipo visual (los datos
// vendrán del server en la Fase 2 backend).
import { useEffect, useRef } from 'react';

export interface CampanaInfo { efecto: string; intensidad?: number; acento?: string | null; saludo?: string | null }

export const CAMPANAS: { v: string; l: string; saludo: string }[] = [
  { v: '', l: 'Ninguna', saludo: '' },
  { v: 'navidad', l: 'Navidad', saludo: 'Feliz Navidad' },
  { v: 'ano_nuevo', l: 'Fin de año', saludo: '¡Feliz Año Nuevo!' },
  { v: 'dia_madre', l: 'Día de la Madre', saludo: 'Feliz Día, Mamá' },
  { v: 'difuntos', l: 'Difuntos / Halloween', saludo: 'Día de los Difuntos' },
  { v: 'carnaval', l: 'Carnaval', saludo: '¡Feliz Carnaval!' },
  { v: 'mundial', l: 'Mundial', saludo: '¡Vamos Ecuador!' },
  { v: 'san_valentin', l: 'San Valentín', saludo: 'Feliz San Valentín' },
  { v: 'black_friday', l: 'Black Friday', saludo: 'BLACK FRIDAY' },
];

interface Cfg { accent: string; tint?: string }
const CFG: Record<string, Cfg> = {
  navidad: { accent: '#c99b3a', tint: 'rgba(255,180,90,0.05)' },
  ano_nuevo: { accent: '#c9a86a' },
  dia_madre: { accent: '#d46a8a', tint: 'rgba(244,169,192,0.05)' },
  difuntos: { accent: '#c65a1a', tint: 'rgba(120,60,140,0.05)' },
  carnaval: { accent: '#e23b7a' },
  mundial: { accent: '#0a4ea3' },
  san_valentin: { accent: '#e0114b', tint: 'rgba(224,17,75,0.05)' },
  black_friday: { accent: '#c6ff00' },
};
// Acento por defecto de cada efecto (para el selector de color del admin)
export const EFECTO_ACENTO: Record<string, string> = Object.fromEntries(Object.entries(CFG).map(([k, v]) => [k, v.accent]));
const N_INT = [26, 46, 68]; // partículas por intensidad 1/2/3

interface P { x: number; y: number; vx: number; vy: number; s: number; rot: number; vr: number; a: number; col: string }
const rnd = (a: number, b: number) => a + Math.random() * (b - a);

export function CampaignOverlay({ campana }: { campana: CampanaInfo }) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const efecto = campana.efecto;
  const intensidad = Math.min(3, Math.max(1, campana.intensidad ?? 2));
  const cfg = CFG[efecto];

  useEffect(() => {
    const canvas = canvasRef.current, wrap = wrapRef.current;
    if (!canvas || !wrap || !cfg) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const acento = campana.acento || cfg.accent;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let W = 0, H = 0, raf = 0, last = 0, t = 0;

    const size = () => {
      const r = wrap.getBoundingClientRect();
      W = Math.max(1, r.width); H = Math.max(1, r.height);
      canvas.width = W; canvas.height = H; // dpr capado a 1 (perf en box barato)
    };
    size();
    const ro = new ResizeObserver(size); ro.observe(wrap);

    // Pool de partículas (nieve/bokeh/pétalos/chispas): viven en bandas laterales
    const N = N_INT[intensidad - 1];
    const usaPart = ['navidad', 'ano_nuevo', 'dia_madre', 'difuntos', 'san_valentin', 'mundial'].includes(efecto);
    const ps: P[] = [];
    const banda = () => (Math.random() < 0.5 ? rnd(0, 0.3) : rnd(0.7, 1)) * W; // izq/der, centro libre
    const nuevaP = (top: boolean): P => {
      const petal = efecto === 'dia_madre' || efecto === 'difuntos';
      const sube = efecto === 'navidad' || efecto === 'san_valentin';
      const col = efecto === 'difuntos' ? (Math.random() < 0.5 ? '#f2932a' : '#e0b33a')
        : efecto === 'dia_madre' ? (Math.random() < 0.5 ? '#f4a9c0' : '#e58aa8')
        : efecto === 'san_valentin' ? (Math.random() < 0.5 ? '#e0114b' : '#f4a9c0')
        : efecto === 'mundial' ? '#ffffff'
        : efecto === 'navidad' ? '#ffd98a' : '#ffe6a0';
      const heart = efecto === 'san_valentin';
      return {
        x: banda(),
        y: sube ? (top ? H + 20 : rnd(0, H)) : (top ? -20 : rnd(-H, 0)),
        vx: rnd(-6, 6), vy: (sube ? -1 : 1) * rnd(petal ? 18 : efecto === 'ano_nuevo' ? 55 : heart ? 10 : 12, petal ? 40 : efecto === 'ano_nuevo' ? 110 : heart ? 22 : efecto === 'mundial' ? 34 : 26),
        s: petal ? rnd(6, 13) : heart ? rnd(9, 17) : efecto === 'mundial' ? rnd(9, 16) : efecto === 'ano_nuevo' ? rnd(1.5, 3.2) : rnd(3, 9),
        rot: heart ? 0 : rnd(0, 6.28), vr: heart ? rnd(0, 6.28) : rnd(-1, 1), a: heart ? rnd(0.3, 0.68) : rnd(0.35, 0.9), col,
      };
    };
    if (usaPart) for (let i = 0; i < N; i++) ps.push(nuevaP(false));

    // Cintas de carnaval (sine scroll en laterales)
    const ribbons = efecto === 'carnaval'
      ? Array.from({ length: intensidad + 2 }, (_, i) => ({ x: (i % 2 ? 0.9 : 0.1) * W + rnd(-30, 30), col: ['#e23b7a', '#f4b400', '#2bb3a3', '#7c3aed', '#e8552e'][i % 5], off: rnd(0, 1000), amp: rnd(14, 30), spd: rnd(30, 60) }))
      : [];

    // Balón de fútbol (centrado en el origen; el llamador aplica transform)
    const ballAtOrigin = (r: number) => {
      ctx.fillStyle = '#fff'; ctx.strokeStyle = '#141414'; ctx.lineWidth = Math.max(1, r * 0.12);
      ctx.beginPath(); ctx.arc(0, 0, r, 0, 6.28); ctx.fill(); ctx.stroke();
      ctx.fillStyle = '#141414'; ctx.beginPath();
      for (let i = 0; i < 5; i++) { const a = -Math.PI / 2 + i * 2 * Math.PI / 5; const px = Math.cos(a) * r * 0.42, py = Math.sin(a) * r * 0.42; if (i) ctx.lineTo(px, py); else ctx.moveTo(px, py); }
      ctx.closePath(); ctx.fill();
      ctx.lineWidth = Math.max(0.6, r * 0.08);
      for (let i = 0; i < 5; i++) { const a = -Math.PI / 2 + i * 2 * Math.PI / 5; ctx.beginPath(); ctx.moveTo(Math.cos(a) * r * 0.42, Math.sin(a) * r * 0.42); ctx.lineTo(Math.cos(a) * r, Math.sin(a) * r); ctx.stroke(); }
    };
    const drawBall = (x: number, y: number, r: number, rot: number) => { ctx.save(); ctx.translate(x, y); ctx.rotate(rot); ballAtOrigin(r); ctx.restore(); };

    const dibujarPart = (p: P, t: number) => {
      ctx.save(); ctx.globalAlpha = p.a; ctx.translate(p.x, p.y); ctx.rotate(p.rot);
      if (efecto === 'mundial') {
        ballAtOrigin(p.s);
      } else if (efecto === 'san_valentin') {
        const s = p.s * (1 + 0.14 * Math.sin(t * 2.4 + p.vr)); // latido sincronizado suave
        ctx.strokeStyle = p.col; ctx.lineWidth = 1.6; ctx.shadowColor = p.col; ctx.shadowBlur = s * 0.5; ctx.beginPath();
        ctx.moveTo(0, s * 0.3);
        ctx.bezierCurveTo(0, -s * 0.1, -s, -s * 0.1, -s, s * 0.35);
        ctx.bezierCurveTo(-s, s * 0.72, 0, s * 0.9, 0, s * 1.15);
        ctx.bezierCurveTo(0, s * 0.9, s, s * 0.72, s, s * 0.35);
        ctx.bezierCurveTo(s, -s * 0.1, 0, -s * 0.1, 0, s * 0.3);
        ctx.stroke();
      } else if (efecto === 'dia_madre' || efecto === 'difuntos') {
        ctx.fillStyle = p.col; ctx.beginPath(); ctx.ellipse(0, 0, p.s, p.s * 0.55, 0, 0, 6.28); ctx.fill();
      } else if (efecto === 'ano_nuevo') {
        ctx.strokeStyle = p.col; ctx.lineWidth = 1.4; ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(0, p.s * 2.2); ctx.stroke();
      } else { // bokeh/nieve
        const g = ctx.createRadialGradient(0, 0, 0, 0, 0, p.s); g.addColorStop(0, p.col); g.addColorStop(1, 'transparent');
        ctx.fillStyle = g; ctx.beginPath(); ctx.arc(0, 0, p.s, 0, 6.28); ctx.fill();
      }
      ctx.restore();
    };

    const frame = (now: number) => {
      const dt = Math.min(0.05, (now - (last || now)) / 1000); last = now; t += dt;
      ctx.clearRect(0, 0, W, H);

      // tinte cálido/atmósfera en esquinas (muy sutil)
      if (cfg.tint) {
        const g = ctx.createRadialGradient(W * 0.85, -H * 0.1, 0, W * 0.85, -H * 0.1, W * 0.7);
        g.addColorStop(0, cfg.tint); g.addColorStop(1, 'transparent'); ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      }

      // niebla baja (difuntos)
      if (efecto === 'difuntos') {
        const fg = ctx.createLinearGradient(0, H, 0, H * 0.72);
        fg.addColorStop(0, 'rgba(150,120,170,0.22)'); fg.addColorStop(1, 'transparent');
        ctx.save(); ctx.globalAlpha = 0.7 + 0.3 * Math.sin(t * 0.6); ctx.fillStyle = fg; ctx.fillRect(0, H * 0.72, W, H * 0.28); ctx.restore();
      }

      // papel picado (difuntos) / banderines: cenefa superior que mece
      if (efecto === 'difuntos' || efecto === 'mundial') {
        const cols = efecto === 'mundial' ? ['#ffd100', '#0a4ea3', '#e8112d'] : ['#f2932a', '#e0b33a', '#c65a1a', '#7c3aed'];
        const bw = W / 16, bh = H * 0.05;
        for (let i = 0; i < 16; i++) {
          ctx.save(); ctx.globalAlpha = 0.55; ctx.fillStyle = cols[i % cols.length];
          const sway = Math.sin(t * 0.8 + i * 0.5) * 3;
          ctx.beginPath(); ctx.moveTo(i * bw, 0); ctx.lineTo((i + 1) * bw, 0); ctx.lineTo(i * bw + bw / 2, bh + sway); ctx.closePath(); ctx.fill(); ctx.restore();
        }
      }

      // partículas
      if (usaPart) for (const p of ps) {
        p.x += p.vx * dt; p.y += p.vy * dt; p.rot += p.vr * dt;
        p.x += Math.sin(t + p.y * 0.02) * 8 * dt; // vaivén
        const fuera = (efecto === 'navidad' || efecto === 'san_valentin') ? p.y < -20 : p.y > H + 20;
        if (fuera) Object.assign(p, nuevaP(true));
        dibujarPart(p, t);
      }

      // cintas carnaval
      for (const rb of ribbons) {
        rb.off += rb.spd * dt; ctx.save(); ctx.globalAlpha = 0.5; ctx.strokeStyle = rb.col; ctx.lineWidth = 5; ctx.beginPath();
        for (let y = -20; y < H + 20; y += 10) { const x = rb.x + Math.sin((y + rb.off) * 0.03) * rb.amp; y === -20 ? ctx.moveTo(x, y) : ctx.lineTo(x, y); }
        ctx.stroke(); ctx.restore();
      }

      // Mundial: portería (arco) con red que se sacude + BALONAZO al arco cada ciclo.
      // (Los balones que caen se dibujan como partículas). Vive en el margen derecho.
      if (efecto === 'mundial') {
        const gw = W * 0.13, gh = H * 0.16, gx = W * 0.85, gy = H * 0.28;
        const l = gx - gw / 2, r2 = gx + gw / 2, tp = gy - gh / 2, bt = gy + gh / 2;
        const CYCLE = 9, SHOT = 1.0, ph = t % CYCLE;
        const impact = (ph >= SHOT * 0.92 && ph < SHOT + 0.45) ? 1 - (ph - SHOT * 0.92) / 0.53 : 0;
        const bulge = (x: number, y: number) => x + impact * gw * 0.55 * Math.max(0, 1 - Math.abs((y - gy) / (gh / 2)));
        // red (se abulta a la derecha cerca del centro en el impacto)
        ctx.save(); ctx.globalAlpha = 0.42; ctx.strokeStyle = '#fff'; ctx.lineWidth = 1;
        for (let i = 1; i < 6; i++) { const x = l + gw * i / 6; ctx.beginPath(); for (let y = tp; y <= bt; y += 6) (y === tp ? ctx.moveTo(bulge(x, y), y) : ctx.lineTo(bulge(x, y), y)); ctx.stroke(); }
        for (let j = 1; j < 7; j++) { const y = tp + gh * j / 7; ctx.beginPath(); for (let x = l; x <= r2; x += 6) (x === l ? ctx.moveTo(bulge(x, y), y) : ctx.lineTo(bulge(x, y), y)); ctx.stroke(); }
        ctx.restore();
        // marco del arco (postes + travesaño)
        ctx.save(); ctx.globalAlpha = 0.9; ctx.strokeStyle = '#fff'; ctx.lineWidth = Math.max(2, W * 0.004);
        ctx.beginPath(); ctx.moveTo(l, bt); ctx.lineTo(l, tp); ctx.lineTo(r2, tp); ctx.lineTo(r2, bt); ctx.stroke(); ctx.restore();
        // destello del gol
        if (impact > 0.6) { ctx.save(); ctx.globalAlpha = (impact - 0.6) * 0.7; ctx.fillStyle = acento; ctx.beginPath(); ctx.arc(gx, gy, gw * 0.5, 0, 6.28); ctx.fill(); ctx.restore(); }
        // el balonazo: parábola desde abajo-derecha hacia el arco (con estela)
        if (ph < SHOT) {
          const u = ph / SHOT, sx0 = W * 0.72, sy0 = H * 0.9;
          const px = (uu: number) => sx0 + (gx - sx0) * uu;
          const py = (uu: number) => sy0 + (gy - sy0) * uu - Math.sin(uu * Math.PI) * H * 0.16;
          for (let k = 4; k >= 1; k--) { const uk = Math.max(0, u - k * 0.045); ctx.save(); ctx.globalAlpha = 0.07 * (5 - k) / 5; drawBall(px(uk), py(uk), W * 0.013, u * 26); ctx.restore(); }
          drawBall(px(u), py(u), W * 0.015 * (0.85 + 0.3 * u), u * 26);
        }
      }

      // Black Friday: marco hairline negro + neón, corchetes en esquinas, barrido
      // diagonal lento y sello -% con anillo punteado que gira. Sobrio y premium.
      if (efecto === 'black_friday') {
        ctx.save(); ctx.globalAlpha = 0.8; ctx.fillStyle = '#0a0a0a';
        ctx.fillRect(0, 0, W, H * 0.05); ctx.fillRect(0, H * 0.95, W, H * 0.05);
        ctx.fillStyle = acento; ctx.globalAlpha = 0.9; ctx.fillRect(0, H * 0.05, W, 1.5); ctx.fillRect(0, H * 0.95 - 1.5, W, 1.5); ctx.restore();
        // corchetes neón en las 4 esquinas
        ctx.save(); ctx.strokeStyle = acento; ctx.lineWidth = 2; ctx.globalAlpha = 0.8;
        const m = W * 0.03, L = W * 0.03;
        const bracket = (x: number, y: number, dx: number, dy: number) => { ctx.beginPath(); ctx.moveTo(x + dx * L, y); ctx.lineTo(x, y); ctx.lineTo(x, y + dy * L); ctx.stroke(); };
        bracket(m, H * 0.11, 1, 1); bracket(W - m, H * 0.11, -1, 1); bracket(m, H * 0.89, 1, -1); bracket(W - m, H * 0.89, -1, -1); ctx.restore();
        // barrido diagonal neón (reemplaza el scanline plano)
        const sx = (((t * 0.06) % 1.3) - 0.15) * W;
        ctx.save(); ctx.globalAlpha = 0.1; const sg = ctx.createLinearGradient(sx - W * 0.08, 0, sx + W * 0.08, 0);
        sg.addColorStop(0, 'transparent'); sg.addColorStop(0.5, acento); sg.addColorStop(1, 'transparent'); ctx.fillStyle = sg;
        ctx.beginPath(); ctx.moveTo(sx - W * 0.08, 0); ctx.lineTo(sx + W * 0.08, 0); ctx.lineTo(sx + W * 0.08 + H * 0.3, H); ctx.lineTo(sx - W * 0.08 + H * 0.3, H); ctx.closePath(); ctx.fill(); ctx.restore();
        // sello -% con anillo punteado giratorio
        ctx.save(); ctx.translate(W * 0.88, H * 0.2); ctx.scale(1 + 0.05 * Math.sin(t * 2.5), 1 + 0.05 * Math.sin(t * 2.5));
        ctx.globalAlpha = 0.95; ctx.fillStyle = '#0a0a0a'; ctx.beginPath(); ctx.arc(0, 0, H * 0.085, 0, 6.28); ctx.fill();
        ctx.strokeStyle = acento; ctx.lineWidth = 2; ctx.setLineDash([H * 0.03, H * 0.02]);
        ctx.save(); ctx.rotate(t * 0.4); ctx.beginPath(); ctx.arc(0, 0, H * 0.105, 0, 6.28); ctx.stroke(); ctx.restore(); ctx.setLineDash([]);
        ctx.fillStyle = acento; ctx.font = `bold ${H * 0.075}px system-ui,sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillText('-%', 0, 0); ctx.restore();
      }

      if (!reduce) raf = requestAnimationFrame(frame);
    };

    if (reduce) { frame(0); } // un solo frame estático
    else raf = requestAnimationFrame(frame);

    const onVis = () => { if (document.hidden) cancelAnimationFrame(raf); else if (!reduce) raf = requestAnimationFrame(frame); };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); document.removeEventListener('visibilitychange', onVis); };
  }, [efecto, intensidad, cfg, campana.acento]);

  if (!efecto || !cfg) return null;
  const acento = campana.acento || cfg.accent;
  return (
    <div ref={wrapRef} style={{ position: 'absolute', inset: 0, zIndex: 4, pointerEvents: 'none', overflow: 'hidden' }}>
      <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
      {/* marco de acento sutil (no toca el centro) */}
      <div style={{ position: 'absolute', inset: 0, boxShadow: `inset 0 0 0 0.4cqh ${acento}55, inset 0 0 8cqh 2cqh ${acento}18` }} />
      {campana.saludo && (
        <div style={{ position: 'absolute', top: '2.4cqh', left: '50%', transform: 'translateX(-50%)', color: acento,
          fontFamily: 'Georgia,serif', fontSize: '1.7cqw', fontWeight: 600, letterSpacing: '.04em', textShadow: '0 1px 3px rgba(0,0,0,.25)' }}>
          {campana.saludo}
        </div>
      )}
    </div>
  );
}
